import { CursorController } from "./core/controllers/CursorController";
import { IStringModule } from "./core/IStringModule";
import { EventManager } from "./core/managers/EventManager";
import { ModuleManager } from "./core/managers/ModuleManager";
import { ObjectManager } from "./core/managers/ObjectManager";
import { ScrollManager } from "./core/managers/ScrollManager";
import { StringContext } from "./core/StringContext";
import { StringData } from "./core/StringData";
import { StringModule } from "./core/StringModule";
import { DefaultToolsContainer } from "./core/StringToolsContainer";
import { StringCursor } from "./modules/cursor/StringCursor";
import { StringImpulse } from "./modules/cursor/StringImpulse";
import { StringAttractor } from "./modules/cursor/StringAttractor";
import { StringMarquee } from "./modules/layout/StringMarquee";
import { StringMasonry } from "./modules/layout/StringMasonry";
import { StringMagnetic } from "./modules/cursor/StringMagnetic";
import { StringSpotlight } from "./modules/cursor/StringSpotlight";
import { StringTilt } from "./modules/cursor/StringTilt";
import { StringVelocity } from "./modules/cursor/StringVelocity";
import { StringLazy } from "./modules/loading/StringLazy";
import { StringLoading } from "./modules/loading/StringLoading";
import { StringInview } from "./modules/screen/StringInview";
import { StringResponsive } from "./modules/screen/StringResponsive";
import { StringAnchor } from "./modules/scroll/StringAnchor";
import { StringGlide } from "./modules/scroll/StringGlide";
import { StringLerp } from "./modules/scroll/StringLerp";
import { StringParallax } from "./modules/scroll/StringParallax";
import { StringProgress } from "./modules/scroll/StringProgress";
import { StringScrollbar } from "./modules/scrollbar/StringScrollbar";
import { StringCircularText } from "./modules/text/StringCircularText";
import { StringSplit } from "./modules/text/StringSplit";
import { StringDelayLerpTracker } from "./modules/tracker/StringDelayLerpTracker";
import { StringFPSTracker } from "./modules/tracker/StringFPSTracker";
import { StringLerpTracker } from "./modules/tracker/StringLerpTracker";
import { StringPositionTracker } from "./modules/tracker/StringPositionTracker";
import { StringObject } from "./objects/StringObject";
import { ScrollMode } from "./states/ScrollState";
import { Debounce } from "./utils/Debounce";
import { EventCallback } from "./models/event/EventCallback";
import { StringFPS } from "./utils/StringFPS";
import { StringSettings } from "./utils/StringSettings";
import { StringVideoAutoplay } from "./modules/loading/StringVideoAutoplay";
import { ScrollMarkRule } from "./models/scroll/ScrollTriggerRule";
import { StringSequence } from "./modules/slider/StringSequence";
import { StringForm } from "./modules/input/StringForm";
import { ISettingsChangeData } from "./models/event/ISettingsChangeData";
import { CenterCache } from "./core/managers/CenterCache";
import { HoverTracker } from "./core/managers/HoverTracker";
import { ScrollScopeRegistry } from "./core/managers/ScrollScopeRegistry";
import { StringSignalHub } from "./core/managers/StringSignalHub";
import { DevtoolsManager } from "./core/managers/DevtoolsManager";
import { CursorReactiveModule } from "./modules/cursor/CursorReactiveModule";
import { StringScroller } from "./modules/scroll/StringScroller";
import { StringScrollContainer } from "./modules/scroll/StringScrollContainer";
import { parsePartOf } from "./utils/ParsePartOf";
import { StringProgressPart } from "./modules/scroll/StringProgressPart";
import { StringSignal } from "./modules/signal/StringSignal";
import { frameDOM } from "./utils/frame-dom";
import { styleTxn } from "./utils/style-txn";
import { startPointerDrag, type PointerDragHandlers, type PointerDragDelta } from "./utils/pointerDrag";
import { StringRandom } from "./modules/random/StringRandom";
import { ScrollController } from "./core/controllers/ScrollController";
// import { logScrollDebug } from "./utils/scroll-debug";
import { StringDevLayout } from "./modules/dev-tools/layout/StringGrid";
import { GridAdapter } from "./modules/dev-tools/layout/adapters/GridAdapter";
import { StringDevRulers } from "./modules/dev-tools/rulers/StringRulers";
import { StringDevInview } from "./modules/dev-tools/inview/StringDevInview";
import { StringDevProgress } from "./modules/dev-tools/progress/StringDevProgress";
import { StringDevModule } from "./modules/dev-tools/core/StringDevModule";
import { StringDevOverlayRegistry } from "./modules/dev-tools/core/StringDevOverlayRegistry";
import { setStringDevStorageScopeToken } from "./modules/dev-tools/core/StringDevStorageScope";
import {
  StringDevIconRegistry,
  resolveDevtoolsIcon,
} from "./modules/dev-tools/core/StringDevIconRegistry";
import {
  buildDevtoolsThemeBlock,
  ensureStringDevtoolsSharedStyles,
  type StringDevStyleTokens,
} from "./modules/dev-tools/core/StringDevStyleSystem";
// import {
//   StringInfiniteVirtual,
//   StringInfiniteVirtualOptions,
//   StringInfiniteVirtualReachEvent,
//   StringInfiniteVirtualSnapshot,
//   StringVirtualAlign,
//   StringVirtualAxis,
//   StringVirtualItem,
//   StringVirtualKey,
//   StringVirtualRange,
// } from "./modules/virtual-scroll/StringInfiniteVirtual";
// import {
//   StringInfiniteVirtualDOM,
//   StringInfiniteVirtualDOMOptions,
// } from "./modules/virtual-scroll/StringInfiniteVirtualDOM";
// import {
//   StringVirtualScroll,
//   StringVirtualScrollItemEventPayload,
//   StringVirtualScrollSetDataPayload,
//   StringVirtualScrollSetFilterPayload,
// } from "./modules/virtual-scroll/StringVirtualScroll";
// import {
//   StringFilterEngine,
//   StringFilterExpression,
//   StringFilterResult,
// } from "./modules/virtual-scroll/filter/StringFilterEngine";
// import {
//   createFilterSchema,
//   StringFilterFieldDefinition,
//   StringFilterFieldKind,
//   StringFilterIndexKind,
//   StringFilterPrimitive,
//   StringFilterSchema,
// } from "./modules/virtual-scroll/filter/StringFilterSchema";
// import {
//   createFilteredVirtualDataset,
//   StringFilteredVirtualDataset,
//   StringFilteredVirtualDatasetItem,
//   StringFilteredVirtualDatasetOptions,
// } from "./modules/virtual-scroll/filter/StringFilteredVirtualDataset";
// import { StringBitset } from "./modules/virtual-scroll/filter/StringBitset";
import type {
  StringRulersTrigger,
  RulersTriggerAction,
} from "./modules/dev-tools/rulers/models/StringRulersTrigger";
import type { RulersLayoutGrid } from "./modules/dev-tools/rulers/models/RulersLayoutGrid";
import { isStringDevtoolProvider } from "./models/devtools/StringDevtool";
import type {
  StringDevtoolDefinition,
  StringDevtoolProvider,
  StringDevtoolState,
} from "./models/devtools/StringDevtool";

function isTouchDevice() {
  return "ontouchstart" in window || navigator.maxTouchPoints > 0;
}
function isSafari(): boolean {
  let ua = navigator.userAgent.toLowerCase();
  if (ua.indexOf("safari") != -1) {
    if (ua.indexOf("chrome") > -1) {
      return false;
    } else {
      return true;
    }
  } else {
    return false;
  }
}

type ScrollIntent =
  | { kind: "position"; position: number; immediate: boolean }
  | { kind: "selector"; selector: string; offset: number; immediate: boolean }
  | { kind: "element"; element: HTMLElement; offset: number; immediate: boolean };

export interface ScrollSettingsOptions {
  /**
   * Controls how quickly the visible scroll position follows the internal target.
   *
   * This is the main "follow" value. Higher values make the viewport catch the target
   * faster and feel more direct. Lower values add softness and visual weight because
   * the target can move ahead while the rendered position eases toward it.
   *
   * Typical range: `0.08` to `0.18`.
   * Default: `0.09`.
   */
  speed?: number;

  /**
   * Controls how strongly wheel input moves the scroll target at the start of a gesture.
   *
   * This is the main "start" value. Higher values make scroll pick up speed quickly
   * from small wheel deltas. Lower values make the start heavier and more gradual.
   * The public value is normalized from `0` to `1` and mapped internally to a safe
   * scroll-force range.
   *
   * Typical range: `0.35` to `0.85`.
   * Default: `0.8`.
   */
  acceleration?: number;

  /**
   * Controls how long the wheel impulse keeps fading after input stops.
   *
   * This is the main "stop" or inertia value. `0` gives a short, direct tail;
   * `0.5` keeps the legacy decay feel; `1` creates a longer and softer glide.
   * It does not expose raw deceleration directly. Instead, StringTune derives a
   * stable decay curve from the current acceleration so the start and stop remain
   * related.
   *
   * Range: `0` to `1`.
   * Default: `0.5`.
   */
  smoothness?: number;

  /**
   * Scales raw wheel input before acceleration is applied.
   *
   * Use this for device/input sensitivity. It changes how much scroll energy enters
   * the system without changing the easing curve itself. Raise it when the page feels
   * underpowered on a mouse wheel; lower it when trackpads feel too aggressive.
   *
   * Typical range: `0.75` to `1.25`.
   * Default: `1.1`.
   */
  multiplier?: number;

  /**
   * Maximum accumulated wheel impulse used in one smooth-scroll step.
   *
   * This prevents high-resolution wheels, trackpads, or bursty browser input from
   * pushing the target too far in a single frame. Lower values feel more controlled;
   * higher values preserve stronger flicks and long fast gestures.
   *
   * Typical range: `1200` to `3200`.
   * Default: `2400`.
   */
  maxDelta?: number;

  /**
   * Minimum visible velocity before smooth scroll snaps to target and stops.
   *
   * Lower values keep the final tail alive longer and make the stop more delicate.
   * Higher values stop earlier, which can feel cleaner on UI-heavy pages where tiny
   * sub-pixel motion is not useful.
   *
   * Typical range: `0.004` to `0.03`.
   * Default: `0.08`.
   */
  stopThreshold?: number;
}

/**
 * Public smooth-scroll tuning facade.
 *
 * Prefer this grouped API for new projects:
 *
 * ```ts
 * stringTune.scroll.configure({
 *   speed: 0.09,
 *   acceleration: 0.8,
 *   smoothness: 0.5,
 *   multiplier: 1.1,
 * });
 * ```
 *
 * The parameters are intentionally named by scroll feel:
 * `acceleration` shapes how motion starts, `speed` shapes how the viewport follows
 * the target, and `smoothness` shapes how motion fades and stops.
 */
export class StringTuneScrollSettings {
  constructor(private readonly data: StringData) {}

  /**
   * Controls how quickly the visible scroll position follows the internal target.
   *
   * This is the visible interpolation factor from current position to target.
   * Higher values feel tighter and more responsive because the viewport catches up
   * quickly. Lower values feel softer, heavier, and more cinematic because the target
   * can move ahead while the visible position eases toward it.
   *
   * This parameter does not change wheel sensitivity. If the scroll starts too fast
   * or too slow, tune `acceleration` or `multiplier` first.
   *
   * Typical range: `0.08` to `0.18`.
   * Default: `0.09`.
   */
  public get speed(): number {
    return this.data.scroll.speed;
  }

  public set speed(value: number) {
    this.data.scroll.speed = Math.max(0.0, value);
  }

  /**
   * Controls how strongly wheel input moves the scroll target at the start of a gesture.
   *
   * This is the primary control for the beginning of a scroll. Higher values make
   * the target react immediately to small wheel deltas. Lower values make the start
   * feel heavier and more gradual.
   *
   * The value is normalized from `0` to `1`. Internally it is mapped to a stable
   * scroll-force range, so consumers do not need to tune raw physics constants.
   *
   * Typical range: `0.35` to `0.85`.
   * Default: `0.8`.
   */
  public get acceleration(): number {
    return this.data.scroll.acceleration;
  }

  public set acceleration(value: number) {
    const normalized = this.clamp01(value);

    this.data.scroll.acceleration = normalized;
    this.data.scroll.speedAccelerate = this.mapNormalizedAcceleration(normalized);
  }

  /**
   * Controls how long the wheel impulse keeps fading after input stops.
   *
   * This is the primary control for the end of a scroll. `0` creates a short,
   * direct tail. `0.5` keeps the legacy decay feel. `1` creates a longer, softer
   * glide after the user stops scrolling.
   *
   * This parameter is not raw deceleration. StringTune derives the internal decay
   * curve from `smoothness` and the current acceleration so tuning remains stable
   * and predictable.
   *
   * Range: `0` to `1`.
   * Default: `0.5`.
   */
  public get smoothness(): number {
    return this.data.scroll.smoothness;
  }

  public set smoothness(value: number) {
    this.data.scroll.smoothness = this.clamp01(value);
    this.data.scroll.decelerationCoefficient = this.getDecayPowerFromSmoothness(value);
  }

  /**
   * Scales raw wheel input before acceleration is applied.
   *
   * Use this when the same scroll curve feels too weak or too strong for the target
   * input device. It changes the amount of wheel energy entering the system, while
   * `acceleration`, `speed`, and `smoothness` keep the same shape.
   *
   * Typical range: `0.75` to `1.25`.
   * Default: `1.1`.
   */
  public get multiplier(): number {
    return this.data.scroll.multiplier;
  }

  public set multiplier(value: number) {
    this.data.scroll.multiplier = Math.max(0.0, value);
  }

  /**
   * Limits a single accumulated wheel impulse before it moves the target.
   *
   * This protects the smooth-scroll engine from large input bursts produced by
   * high-resolution wheels, fast trackpad gestures, or browser event batching.
   * Lower values feel more controlled and restrained. Higher values allow stronger
   * flick gestures to travel farther.
   *
   * Typical range: `1200` to `3200`.
   * Default: `2400`.
   */
  public get maxDelta(): number {
    return this.data.scroll.maxDelta;
  }

  public set maxDelta(value: number) {
    this.data.scroll.maxDelta = Math.max(1.0, value);
  }

  /**
   * Minimum visible velocity before smooth scroll snaps to target and stops.
   *
   * This controls the final settle point. Lower values preserve tiny end-of-motion
   * movement and make the stop more delicate. Higher values stop earlier and can
   * feel cleaner on dense interfaces where long sub-pixel motion adds noise.
   *
   * Typical range: `0.004` to `0.03`.
   * Default: `0.08`.
   */
  public get stopThreshold(): number {
    return this.data.scroll.stopThreshold;
  }

  public set stopThreshold(value: number) {
    this.data.scroll.stopThreshold = Math.max(0.0, value);
  }

  /**
   * Updates several smooth-scroll settings at once.
   *
   * Omitted properties keep their current values, so this can be used for presets
   * or partial runtime tuning without resetting the whole scroll feel.
   *
   * ```ts
   * stringTune.scroll.configure({
   *   speed: 0.105,
   *   acceleration: 0.55,
   *   smoothness: 0.72,
   * });
   * ```
   */
  public configure(options: ScrollSettingsOptions): void {
    if (options.speed !== undefined) this.speed = options.speed;
    if (options.acceleration !== undefined) this.acceleration = options.acceleration;
    if (options.smoothness !== undefined) this.smoothness = options.smoothness;
    if (options.multiplier !== undefined) this.multiplier = options.multiplier;
    if (options.maxDelta !== undefined) this.maxDelta = options.maxDelta;
    if (options.stopThreshold !== undefined) this.stopThreshold = options.stopThreshold;
  }

  /**
   * Legacy bridge for `stringTune.decelerationCoefficient`.
   * Prefer `stringTune.scroll.smoothness` for new code.
   */
  public setLegacyDecelerationCoefficient(value: number): void {
    const coefficient = Math.max(0.0, value);

    this.data.scroll.decelerationCoefficient = coefficient;
    this.data.scroll.smoothness = this.getSmoothnessFromDecayPower(coefficient);
  }

  private mapNormalizedAcceleration(value: number): number {
    const min = 0.1;
    const max = 0.5;

    return min + (max - min) * value;
  }

  private getDecayPowerFromSmoothness(value: number): number {
    return 1.0 + (0.5 - this.clamp01(value)) * 1.2;
  }

  private getSmoothnessFromDecayPower(value: number): number {
    return this.clamp01(0.5 - (value - 1.0) / 1.2);
  }

  private clamp01(value: number): number {
    return Math.min(Math.max(0.0, value), 1.0);
  }
}

class StringTune {
  private static readonly DEVTOOLS_ACCESS_URL = "https://access.fiddle.digital/";
  private static readonly DEVTOOLS_LOG_PREFIX = "[StringTune Devtools]";
  private static readonly DEVTOOLS_ARTIFACT_SELECTORS = [
    "[data-string-dev-viewport-layer]",
    "[data-string-dev-viewport-world]",
    "[data-stdg-dock]",
    "[data-stdg-dock-sub-badges]",
  ];

  /** Bound handler for the scroll start event */
  private onScrollStartBind: any;

  /** Bound handler for the scroll stop event */
  private onScrollStopBind: any;

  /** Bound handler for the scroll direction change event */
  private onDirectionChangeBind: any;

  /** Bound handler for scroll mode/config changes */
  private onScrollConfigChangeBind: any;

  /** Bound wheel event handler */
  private onWheelBind: any;

  /** Bound scroll event handler */
  private onScrollBind: any;

  /** Bound resize event handler */
  private onResizeBind: any;

  /** Bound mouse move handler */
  private onMouseMoveBind: any;

  /** Bound scroll to handler */
  private onScrollToBind: (value: any) => void;
  private onDOMChangedBind: (data?: { stringElementChanged?: boolean }) => void;

  private onContainerTransitionEndBind: any;
  private onResizeObserverBind: any;
  private pendingScroll: boolean = false;
  private lastScrollEmitted: number = NaN;
  private observerContainerMutation: MutationObserver | null = null;
  private pendingResizeRaf: number | null = null;
  private pendingResizeForce: boolean = false;
  private pendingRebuildRaf: number | null = null;
  private activeScrollIntent: ScrollIntent | null = null;

  /** Singleton instance of StringTune */
  private static i: StringTune;

  /** Root scrollable element (typically <body>) */
  private root: any;

  /** Window object (used for event bindings and dimensions) */
  private window: any;

  /** Previous window width for resize diff check */
  private prevWidth: number = 0;

  /** Previous window height for resize diff check */
  private prevHeight: number = 0;

  /** Manages all modules registered in the system */
  private moduleManager: ModuleManager;

  /** Manages scroll modes and active scroll engine */
  private scrollManager: ScrollManager;

  /** Manages all interactive objects (elements with `string-*` attributes) */
  private objectManager: ObjectManager;

  /** Central event manager for internal pub-sub logic */
  private eventManager: EventManager;
  private signalHub: StringSignalHub;

  /** Handles custom cursor logic (if enabled) */
  private cursorController: CursorController;

  /** Provides default utility tools (parsers, interpolation, etc.) */
  private tools: DefaultToolsContainer;

  /** Main loop used for frame updates (with fixed FPS) */
  private loop: StringFPS = new StringFPS();

  /** Global reactive data store (scroll, viewport, etc.) */
  private data: StringData;

  private scrollSettings: StringTuneScrollSettings;

  /** Context shared across all modules (events, data, tools, settings) */
  private context: StringContext;

  /** Caches the center positions of string objects. */
  private centers: CenterCache;

  /** Registry of scroll scopes (root page scroll + nested StringScrollContainer instances). */
  private scrollScopes: ScrollScopeRegistry;

  /** Tracks hover states of string objects. */
  private hoverManager: HoverTracker;
  private devtools: DevtoolsManager;
  private devtoolsFpsLastSampleTime: number = 0;
  private devtoolsFpsFrameCount: number = 0;

  private observerContainerResize: ResizeObserver | null = null;
  private devtoolsAccessToken: string = "";
  private devtoolsAccessState: "unknown" | "pending" | "granted" | "denied" = "unknown";
  private devtoolsAccessRequestId: number = 0;
  private pendingDevtoolUses: Array<{ objectClass: typeof StringModule; settings: any }> = [];
  private hasStarted: boolean = false;
  private devtoolsAccessLastMessage: "none" | "granted" | "denied" | "error" = "none";

  public canRebuild: boolean = true;

  /**
   * Sets the scroll position manually.
   * This overrides all internal scroll states including target and lerped values.
   * Useful for programmatic jumps or syncing scroll externally.
   *
   * @param value The new scroll position in pixels.
   */
  public set scrollPosition(value: number) {
    this.data.scroll.current = value;
    this.data.scroll.target = value;
    this.data.scroll.transformedCurrent =
      this.data.scroll.current * this.data.viewport.transformScale;
    this.data.scroll.delta = 0;
    this.data.scroll.lerped = 0;
    this.scrollManager.updatePosition();
    this.moduleManager.onScroll();
    this.objectManager.checkInview();
  }

  public set accessDevtoolToken(value: string) {
    const normalized = value.trim();
    const isSameToken = normalized === this.devtoolsAccessToken;

    if (
      isSameToken &&
      (this.devtoolsAccessState === "granted" || this.devtoolsAccessState === "pending")
    ) {
      return;
    }

    this.devtoolsAccessToken = normalized;

    if (normalized.length === 0) {
      this.devtoolsAccessState = "unknown";
      return;
    }

    this.validateDevtoolsAccess(normalized);
  }

  /**
   * Configures the container element(s) used for scroll tracking.
   * Accepts either the `Window` object or an `HTMLElement`.
   * Determines the appropriate internal element references based on the input type
   * and triggers a resize calculation.
   *
   * @param {Window | HTMLElement | any} container The target window or HTML element to associate with scrolling.
   * Handles `Window`, `HTMLElement`, and potentially other types via fallback.
   */
  public set scrollContainer(container: any) {
    const previousScrollContainer = this.data.scroll.scrollContainer;
    const previousWheelContainer = this.data.scroll.container;

    if (this.hasStarted) {
      previousScrollContainer?.removeEventListener("scroll", this.onScrollBind);
      previousWheelContainer?.removeEventListener("wheel", this.onWheelBind);
    }

    this.observerContainerResize?.unobserve(this.context.data.scroll.container);

    this.data.scroll.elementContainer.removeEventListener(
      "transitionend",
      this.onContainerTransitionEndBind,
    );
    if (container instanceof Window) {
      this.data.scroll.container = document.body;
      this.data.scroll.elementContainer = document.documentElement;
      this.data.scroll.scrollContainer = container;
    } else if (container instanceof HTMLElement) {
      this.data.scroll.container = container;
      this.data.scroll.elementContainer = container;
      this.data.scroll.scrollContainer = container;
    } else {
      // Fallback case
      this.data.scroll.container = document.body;
      this.data.scroll.elementContainer = document.documentElement;
      this.data.scroll.scrollContainer = container;
    }
    this.data.scroll.elementContainer.addEventListener(
      "transitionend",
      this.onContainerTransitionEndBind,
    );
    this.observerContainerResize?.observe(this.context.data.scroll.container);
    this.observeContainerMutations();

    if (this.hasStarted) {
      this.data.scroll.scrollContainer?.addEventListener("scroll", this.onScrollBind);
      this.data.scroll.container?.addEventListener("wheel", this.onWheelBind, {
        passive: false,
      });
    }

    this.queueResize(true, "scrollContainer");
  }

  /**
   * Gets the current scroll position in pixels.
   * This is typically updated every frame.
   */
  public get scrollPosition() {
    return this.data.scroll.current;
  }

  public get scrollHeight() {
    return this.data.viewport.contentHeight;
  }

  public get containerHeight() {
    return this.data.viewport.windowHeight;
  }

  /**
   * Smooth scroll tuning API.
   * Use this instead of legacy top-level scroll setters for new code.
   */
  public get scroll(): StringTuneScrollSettings {
    return this.scrollSettings;
  }

  /**
   * Sets the base scroll speed for smooth scrolling.
   * Typically a value between 0 and 1.
   */
  public set speed(value: number) {
    this.scroll.speed = value;
  }

  /**
   * Sets the scroll acceleration using a normalized value from 0 to 1.
   * Internally maps it to a real acceleration value between 0.1 and 0.5.
   *
   * @param speed A normalized acceleration factor (0 to 1).
   */
  public set speedAccelerate(speed: number) {
    this.scroll.acceleration = speed;
  }

  /**
   * @deprecated Use `stringTune.scroll.smoothness` instead.
   */
  public set decelerationCoefficient(coefficient: number) {
    this.scroll.setLegacyDecelerationCoefficient(coefficient);
  }

  /**
   * Sets the scroll mode for desktop devices.
   * Can be 'smooth', 'default', or 'disable'.
   */
  public set scrollDesktopMode(mode: ScrollMode) {
    this.scrollManager.setDesktopMode(mode);
  }

  /**
   * Sets the scroll mode for mobile devices.
   * Can be 'smooth', 'default', or 'disable'.
   */
  public set scrollMobileMode(mode: ScrollMode) {
    this.scrollManager.setMobileMode(mode);
  }

  /**
   * Locks page scroll without changing desktop/mobile mode preferences.
   * Use when opening modals — scroll containers remain functional.
   */
  public lockPageScroll(): void {
    this.scrollManager.lockPageScroll();
  }

  /**
   * Unlocks page scroll, restoring the mode based on current device (desktop/mobile).
   */
  public unlockPageScroll(): void {
    this.scrollManager.unlockPageScroll();
  }

  public set FPSTrackerVisible(visible: boolean) {
    this.data.system.fpsTracker = visible;
    this.eventManager.emit("tracker:fps:visible", visible);
  }

  public set PositionTrackerVisible(visible: boolean) {
    this.data.system.positionTracker = visible;
    this.eventManager.emit("tracker:position:visible", visible);
  }

  public set domBatcherEnabled(enabled: boolean) {
    this.objectManager.setDOMBatcherEnabled(enabled);
  }

  public set intersectionObserverEnabled(enabled: boolean) {
    this.objectManager.setIntersectionObserverEnabled(enabled);
  }

  private debouncedResize = Debounce((source: string) => {
    this.queueResize(false, source);
  }, 30);

  private debouncedRebuild = Debounce(() => {
    this.queueRebuild();
  }, 30);

  private constructor() {
    this.cleanupExistingDevtoolsArtifacts();
    this.root = document.body;
    this.window = window;

    this.tools = new DefaultToolsContainer();
    this.data = new StringData();
    this.scrollSettings = new StringTuneScrollSettings(this.data);
    this.eventManager = new EventManager();
    this.signalHub = new StringSignalHub((eventName, payload) =>
      this.eventManager.emit(eventName, payload),
    );
    this.moduleManager = new ModuleManager(this.data);
    this.objectManager = new ObjectManager(
      this.data,
      this.moduleManager,
      this.eventManager,
      this.tools,
    );

    this.scrollScopes = new ScrollScopeRegistry(window);
    this.centers = new CenterCache(this.scrollScopes);
    this.hoverManager = new HoverTracker();
    this.devtools = new DevtoolsManager();

    this.context = {
      events: this.eventManager,
      data: this.data,
      tools: this.tools,
      settings: {},
      centers: this.centers,
      hover: this.hoverManager,
      objectManager: this.objectManager,
      signals: this.signalHub,
      scrollScopes: this.scrollScopes,
    };

    this.cursorController = new CursorController(1, this.context);
    this.scrollManager = new ScrollManager(this.context);

    this.setupSettings({
      "global-class": false,
      "offset-top": "0%",
      "offset-bottom": "0%",
      key: "--progress",
      "inview-top": "0%",
      "inview-bottom": "0%",
      "enter-el": "top",
      "enter-vp": "bottom",
      "exit-el": "bottom",
      "exit-vp": "top",
      "parallax-bias": "0.0",
      parallax: "0.2",
      lerp: "0.2",
      "cursor-lerp": "0.75",
      radius: "150",
      strength: "0.3",
      glide: "1",
      anchor: "center center",
      timeout: 900,
      alignment: "center",
      "target-disable": "false",
      "target-style-disable": "false",
      "target-class": "",
      active: "false",
      fixed: "false",
      repeat: "false",
      "self-disable": "false",
      abs: "false",
      easing: "cubic-bezier(0.25, 0.25, 0.25, 0.25)",
      "glide-base-velocity": 0.00125,
      "glide-reduce-velocity": 0.0000625,
      "glide-negative-velocity": -0.0001,

      "position-strength": 3,
      "position-tension": 0.05,
      "position-friction": 0.15,
      "position-max-velocity": 10,
      "position-update-threshold": 0.1,
      "rotation-strength": 0.75,
      "rotation-tension": 0.06,
      "rotation-friction": 0.18,
      "rotation-max-angular-velocity": 6,
      "rotation-max-angle": 18,
      "rotation-update-threshold": 0.15,
      "max-offset": 220,
      "sleep-epsilon": 0.01,
      "continuous-push": true,
    });

    this.onContainerTransitionEndBind = this.onContainerTransitionEnd.bind(this);

    this.onResizeObserverBind = this.onResizeObserverEvent.bind(this);
    this.observerContainerResize = new ResizeObserver(this.onResizeObserverBind);
    this.observerContainerResize.observe(this.context.data.scroll.container);

    this.onWheelBind = this.onWheelEvent.bind(this);
    this.onScrollBind = this.onScrollEvent.bind(this);
    this.onResizeBind = () => {
      this.queueResize(false, "resizeBind");
    };
    this.onMouseMoveBind = this.onMouseMoveEvent.bind(this);

    this.onScrollStartBind = this.onScrollStart.bind(this);
    this.onScrollStopBind = this.onScrollStop.bind(this);
    this.onDirectionChangeBind = this.onDirectionChange.bind(this);
    this.onScrollConfigChangeBind = this.onScrollConfigChange.bind(this);
    this.onScrollToBind = this.scrollTo.bind(this);
    this.onDOMChangedBind = this.onDOMChanged.bind(this);

    this.eventManager.on(`wheel`, this.onWheelBind);
    this.eventManager.on(`resize`, this.onResizeBind);
    this.eventManager.on(`scrollTo`, this.onScrollToBind);
    this.eventManager.on(`dom:changed`, this.onDOMChangedBind);

    this.scrollManager.bindEvents({
      onScrollStart: this.onScrollStartBind,
      onScrollStop: this.onScrollStopBind,
      onDirectionChange: this.onDirectionChangeBind,
      onModeChange: this.onScrollConfigChangeBind,
    });

    this.loop.setOnFrame((time: number) => {
      this.data.time.delta = time - this.data.time.now;
      this.data.time.previous = this.data.time.now;
      this.data.time.now = time;
      this.data.time.elapsed += this.data.time.delta;
      this.onUpdateEvent();
      this.updateDevtoolsFPS(time);
    });
    this.on("image:load:all", () => {
      this.onRebuild();
    });
    this.scrollContainer = window;
  }

  /**
   * Returns the singleton instance of StringTune.
   * If not already created, initializes it.
   */
  public static getInstance(): StringTune {
    if (!StringTune.i) {
      StringTune.i = new StringTune();
    }
    return StringTune.i;
  }

  /**
   * Finds and returns an existing module by its class.
   * Useful for reusing a module instance without re-registering.
   *
   * @template T The type of the module to retrieve.
   * @param type The module class constructor.
   * @returns The module instance if found, otherwise undefined.
   */
  public reuse<T>(type: new (...args: any[]) => T): T | undefined {
    return this.moduleManager.find(type);
  }

  /**
   * Instantiates and registers a new module.
   * Accepts optional per-instance settings that override global settings.
   *
   * @param objectClass The module class to instantiate.
   * @param settings Optional settings specific to this module.
   */
  public use(objectClass: typeof StringModule, settings: any = null) {
    const existing = this.moduleManager.find(objectClass);
    if (existing) {
      return;
    }
    if (this.shouldDeferDevtoolModule(objectClass, settings)) {
      return;
    }
    this.instantiateModule(objectClass, settings);
  }

  private cleanupExistingDevtoolsArtifacts(): void {
    for (const selector of StringTune.DEVTOOLS_ARTIFACT_SELECTORS) {
      document.querySelectorAll(selector).forEach((node) => node.remove());
    }
  }

  private instantiateModule(objectClass: typeof StringModule, settings: any = null): void {
    const effectiveSettings = {
      ...this.context.settings,
      ...settings,
    };
    const module = new objectClass({
      events: this.eventManager,
      data: this.data,
      tools: this.tools,
      settings: effectiveSettings,
      centers: this.centers,
      hover: this.hoverManager,
      objectManager: this.objectManager,
      signals: this.signalHub,
      scrollScopes: this.scrollScopes,
    });
    this.moduleManager.register(module);
    if (isStringDevtoolProvider(module)) {
      this.devtools.register(module.getDevtoolDefinition());
    }

    if (this.hasStarted) {
      this.objectManager.attachModule(module);
      module.onInit();
      module.onResize();
      module.onScroll(this.data);
      module.onFrame(this.data);
    }
  }

  private shouldDeferDevtoolModule(objectClass: typeof StringModule, settings: any): boolean {
    const isDevtoolModule =
      objectClass === StringDevModule || objectClass.prototype instanceof StringDevModule;
    if (!isDevtoolModule) {
      return false;
    }

    if (this.devtoolsAccessState === "granted") {
      return false;
    }

    this.pendingDevtoolUses.push({ objectClass, settings });
    if (this.devtoolsAccessToken.length > 0 && this.devtoolsAccessState !== "pending") {
      this.validateDevtoolsAccess(this.devtoolsAccessToken);
    }
    return true;
  }

  private async validateDevtoolsAccess(token: string): Promise<void> {
    const requestId = ++this.devtoolsAccessRequestId;
    this.devtoolsAccessState = "pending";

    try {
      const response = await fetch(
        `${StringTune.DEVTOOLS_ACCESS_URL}?token=${encodeURIComponent(token)}`,
      );
      const allowed = await this.resolveDevtoolsAccessResponse(response);
      if (requestId !== this.devtoolsAccessRequestId || token !== this.devtoolsAccessToken) {
        return;
      }

      this.devtoolsAccessState = allowed ? "granted" : "denied";
      if (!allowed) {
        this.logDevtoolsAccess("denied");
        this.pendingDevtoolUses = [];
        return;
      }

      this.logDevtoolsAccess("granted");
      const pending = [...this.pendingDevtoolUses];
      this.pendingDevtoolUses = [];
      pending.forEach(({ objectClass, settings }) => {
        this.instantiateModule(objectClass, settings);
      });
    } catch {
      if (requestId !== this.devtoolsAccessRequestId || token !== this.devtoolsAccessToken) {
        return;
      }
      if (this.devtoolsAccessState === "granted") {
        return;
      }
      this.devtoolsAccessState = "denied";
      this.logDevtoolsAccess("error");
      this.pendingDevtoolUses = [];
    }
  }

  private logDevtoolsAccess(type: "granted" | "denied" | "error"): void {
    if (this.devtoolsAccessLastMessage === type) {
      return;
    }
    this.devtoolsAccessLastMessage = type;

    if (type === "granted") {
      console.info(
        `${StringTune.DEVTOOLS_LOG_PREFIX} Access granted. Devtools modules are enabled.`,
      );
      return;
    }

    if (type === "denied") {
      console.warn(
        `${StringTune.DEVTOOLS_LOG_PREFIX} Access denied. Devtools modules were not enabled. Check accessDevtoolToken.`,
      );
      return;
    }

    console.warn(
      `${StringTune.DEVTOOLS_LOG_PREFIX} Access check failed. Devtools modules were not enabled.`,
    );
  }

  private async resolveDevtoolsAccessResponse(response: Response): Promise<boolean> {
    if (!response.ok) {
      return false;
    }

    const contentType = response.headers.get("content-type")?.toLowerCase() ?? "";
    if (contentType.includes("application/json")) {
      const data = await response.json();
      if (typeof data === "boolean") {
        return data;
      }
      if (data && typeof data === "object" && "allowed" in data) {
        return data.allowed === true;
      }
      return false;
    }

    const text = (await response.text()).trim().toLowerCase();
    return text === "true";
  }

  /**
   * Registers a new scroll mode (provider) to the system.
   * Allows integrating custom scroll implementations (e.g. Lenis, Locomotive).
   *
   * @param name The unique name for this scroll mode (e.g. 'smooth', 'lenis').
   * @param factory A function that receives the StringContext and returns a ScrollController instance, OR a ScrollController class constructor.
   *
   * Example:
   * ```ts
   * stringTune.registerScrollMode("custom", (context) => new CustomAdapter(context));
   * ```
   */
  public registerScrollMode(
    name: string,
    factory:
      | ((context: StringContext) => ScrollController)
      | (new (context: StringContext) => ScrollController),
  ) {
    let controller: ScrollController;

    // Check if it's a class constructor (handles class fields too)
    if (typeof factory === "function" && factory.prototype instanceof ScrollController) {
      const Constructor = factory as new (context: StringContext) => ScrollController;
      controller = new Constructor(this.context);
    } else {
      const func = factory as (context: StringContext) => ScrollController;
      controller = func(this.context);
    }

    if (!controller.name) {
      controller.name = name;
    }

    this.scrollManager.registerMode(name, controller);
  }

  /**
   * Subscribes to a global event within the system.
   *
   * @param eventName The name of the event to listen for.
   * @param callback The function to call when the event is triggered.
   * @param id Optional subscription ID (for easier management).
   */
  public on(eventName: string, callback: EventCallback<any>, id: string = "") {
    this.eventManager.on(eventName, callback, id);
  }
  public emit(eventName: string, data: any) {
    this.eventManager.emit(eventName, data);
  }

  /**
   * Unsubscribes from a global event.
   *
   * @param eventName The name of the event.
   * @param callback The previously registered callback.
   * @param id Optional ID used during subscription.
   */
  public off(eventName: string, callback: EventCallback<any>, id: string = "") {
    this.eventManager.off(eventName, callback, id);
  }

  /**
   * Adds a scroll trigger rule that activates when the user scrolls past a defined offset
   * in a specific direction. This can be used to toggle CSS classes or execute callbacks
   * when elements come into view or go out of view.
   *
   * @param rule - The scroll trigger configuration object.
   *   - `id`: A unique identifier for this rule.
   *   - `offset`: The vertical scroll offset (in pixels) where the rule should activate.
   *   - `direction`: Defines the scroll direction required to activate the rule.
   *                 Can be `"forward"`, `"backward"`, or `"any"`.
   *   - `onEnter`: (Optional) A function that will be called when the scroll position enters the trigger zone
   *                in the specified direction.
   *   - `onLeave`: (Optional) A function that will be called when the scroll position leaves the trigger zone
   *                or scrolls in the opposite direction.
   *   - `toggleClass`: (Optional) An object defining a class toggle behavior.
   *                    It contains a target element and a class name to be added when the trigger is active
   *                    and removed when it's not.
   */
  public addScrollMark(rule: ScrollMarkRule) {
    this.scrollManager.addScrollMark(rule);
  }

  /**
   * Removes a scroll trigger by its unique identifier.
   *
   * @param id - The unique identifier of the scroll trigger to be removed.
   */
  public removeScrollMark(id: string) {
    this.scrollManager.removeScrollMark(id);
  }

  /**
   * Starts the scroll engine and initializes all listeners, observers, and modules.
   *
   * @param fps Desired frames per second for the update loop.
   */
  public start(fps: number) {
    if (this.hasStarted) {
      return;
    }
    this.hasStarted = true;
    this.data.scroll.scrollContainer?.addEventListener("scroll", this.onScrollBind);
    this.data.scroll.container?.addEventListener("wheel", this.onWheelBind, {
      passive: false,
    });

    window.addEventListener("resize", this.onResizeBind);
    this.root.addEventListener("mousemove", this.onMouseMoveBind);
    this.observeContainerMutations();

    this.use(StringInview);

    const htmlFontSize = window.getComputedStyle(document.documentElement).fontSize;
    const fontSizeNumber = parseFloat(htmlFontSize);
    this.context.data.viewport.baseRem = fontSizeNumber;

    document.documentElement.classList.add("-string");
    this.syncDebugScrollState();
    this.moduleManager.onInit();
    this.onResize(false, "start");
    this.initObjects();
    this.objectManager.observeDOM();

    this.loop.start(fps);
    this.eventManager.emit(`start`, null);
  }

  /**
   * Initializes all DOM elements with `string` or `string-copy-from` attributes.
   * Registers them with the object manager and triggers resize/scroll/frame hooks.
   */
  private initObjects() {
    document.querySelectorAll("[string],[data-string]").forEach((element) => {
      this.objectManager.add(element as HTMLElement);
    });
    document.querySelectorAll("[string-copy-from],[data-string-copy-from]").forEach((element) => {
      let connectTargetId = this.tools.domAttribute.process({
        element: element as HTMLElement,
        key: "copy-from",
        fallback: "",
      });

      if (connectTargetId && connectTargetId.length > 0) {
        this.objectManager.linkMirror(connectTargetId, element as HTMLElement);
      }
    });

    this.moduleManager.onResize();
    this.moduleManager.onScroll();
    this.moduleManager.onFrame();
  }

  /**
   * Sets global fallback settings for all modules.
   * These can be overridden by module-specific settings during `use(...)`.
   *
   * @param settings A key-value map of default settings (e.g. 'offset-top': '-10%').
   */
  public setupSettings(settings: StringSettings): void {
    this.context.settings = {
      ...this.context.settings,
      ...settings,
    };
    if (typeof settings["storageToken"] === "string") {
      setStringDevStorageScopeToken(settings["storageToken"]);
    } else if (typeof settings["storage-token"] === "string") {
      setStringDevStorageScopeToken(settings["storage-token"]);
    }
    if (typeof settings["accessDevtoolToken"] === "string") {
      this.accessDevtoolToken = settings["accessDevtoolToken"];
    }
    this.onSettingsChange({
      isDesktop: this.data.viewport.windowWidth > 1024,
      widthChanged: true,
      heightChanged: true,
      scrollHeightChanged: true,
      isForceRebuild: false,
    });
  }

  private onResizeObserverEvent() {
    this.debouncedResize("resizeObserver");
  }

  private onContainerTransitionEnd(event: TransitionEvent) {
    if (event.target === this.context.data.scroll.container) {
      this.queueResize(true, "containerTransition");
    }
  }

  private onDOMChanged(data?: { stringElementChanged?: boolean }) {
    const force = data?.stringElementChanged === true;
    this.queueResize(force, "domChanged");
  }

  private observeContainerMutations() {
    this.observerContainerMutation?.disconnect();
    const container = this.context.data.scroll.container;
    if (!container) return;

    this.observerContainerMutation = new MutationObserver((mutationsList: MutationRecord[]) => {
      for (let i = 0; i < mutationsList.length; i++) {
        const mutation = mutationsList[i];
        if (
          mutation.type === "attributes" &&
          (mutation.attributeName === "style" || mutation.attributeName === "class")
        ) {
          this.debouncedResize("containerMutation");
          break;
        }
      }
    });

    this.observerContainerMutation.observe(container, {
      attributes: true,
      attributeFilter: ["style", "class"],
    });
  }

  private queueResize(force: boolean = false, source: string = ""): void {
    if (force) {
      this.pendingResizeForce = true;
    }

    if (this.pendingResizeRaf != null) {
      return;
    }

    this.pendingResizeRaf = requestAnimationFrame(() => {
      this.pendingResizeRaf = null;
      const shouldForce = this.pendingResizeForce;
      this.pendingResizeForce = false;
      this.onResize(shouldForce, source);
    });
  }

  private queueRebuild(): void {
    if (this.pendingRebuildRaf != null) {
      return;
    }

    this.pendingRebuildRaf = requestAnimationFrame(() => {
      this.pendingRebuildRaf = null;
      this.onRebuild();
    });
  }

  public onRebuild(): void {
    const scroll = this.context.data.scroll;
    const newScrollHeight = scroll.container.scrollHeight;

    if (this.context.data.viewport.contentHeight === newScrollHeight) {
      return;
    }

    this.context.data.viewport.contentHeight = newScrollHeight;
    scroll.bottomPosition = newScrollHeight - this.context.data.viewport.windowHeight;

    this.moduleManager.onRebuild();
  }

  /**
   * Handles mouse move event and dispatches it to cursor and modules.
   * @param e Native mouse move event.
   */
  private onMouseMoveEvent(e: MouseEvent) {
    this.cursorController.onMouseMove(e);
    this.moduleManager.onMouseMove(e);
    frameDOM.measure(() => {
      this.moduleManager.onMouseMoveMeasure();
    });
  }

  /**
   * Handles wheel scroll event and passes it to the scroll engine and modules.
   * @param e Native wheel event.
   */
  private onWheelEvent(e: WheelEvent) {
    const target = e.target as HTMLElement;
    const isModal = target.closest("[string-isolation],[data-string-isolation]");
    if (isModal != null) return;
    this.clearActiveScrollIntent("wheel");
    this.scrollManager.get().onWheel(e);
    this.moduleManager.onWheel(e);
  }

  /**
   * Called when scrolling begins.
   * Triggers module scroll start lifecycle hook.
   */
  private onScrollStart() {
    this.moduleManager.onScrollStart();
    this.eventManager.emit(`scroll:start`, null);
  }

  /**
   * Called when scrolling ends.
   * Triggers module scroll stop lifecycle hook.
   */
  private onScrollStop() {
    this.clearActiveScrollIntent("scroll-stop");
    this.moduleManager.onScrollStop();
    this.eventManager.emit(`scroll:stop`, null);
  }

  /**
   * Called when scrolling ends.
   * Triggers module scroll stop lifecycle hook.
   */
  private onDirectionChange() {
    this.moduleManager.onDirectionChange();
  }

  private onScrollConfigChange() {
    this.moduleManager.onScrollConfigChange();
    this.syncDebugScrollState();
    this.moduleManager.onScroll();
    this.moduleManager.onScrollMeasure();
    this.moduleManager.onFrame();
    styleTxn.run(() => {
      this.moduleManager.onMutate();
    });
  }

  private syncDebugScrollState(): void {
    const html = document.documentElement;
    const isMobileBucket = window.innerWidth < 1024;
    html.setAttribute("data-string-scroll-mode", String(this.data.scroll.mode));
    html.setAttribute("data-string-scroll-device", isMobileBucket ? "mobile" : "desktop");
  }

  /**
   * Called when global or module settings are updated.
   * Notifies all managers and modules to re-read new settings.
   */
  private onSettingsChange(data: ISettingsChangeData) {
    this.cursorController.onSettingsChange(data);
    this.objectManager.onSettingsChange(data);
    this.moduleManager.onSettingsChange(data);
  }

  /**
   * Handles native scroll event.
   * Prevents default behavior and triggers internal scroll logic and event emissions.
   *
   * @param e The native scroll event.
   */
  private onScrollEvent(e: Event) {
    e.preventDefault();

    this.context.centers.invalidateAll();

    this.scrollManager.get().onScroll(e);
    if (this.data.scroll.mode !== "smooth") {
      this.clearActiveScrollIntent("native-scroll-non-smooth");
    }
    this.pendingScroll = true;
    return false;
  }

  /**
   * Called every frame by the update loop.
   * Triggers scroll engine, modules, and global `update` event.
   */
  private onUpdateEvent() {
    this.cursorController.onFrame();
    this.scrollManager.get().onFrame();
    this.moduleManager.onFrame();

    if (this.pendingScroll || this.data.scroll.current !== this.lastScrollEmitted) {
      this.pendingScroll = false;
      this.moduleManager.onScroll();
      this.objectManager.checkInview();
      this.eventManager.emit(`lerp`, this.data.scroll.lerped);
      this.eventManager.emit(`scroll`, this.data.scroll.current);
      frameDOM.measure(() => {
        this.moduleManager.onScrollMeasure();
      });
      this.lastScrollEmitted = this.data.scroll.current;
    }

    frameDOM.mutate(() => {
      styleTxn.begin();
      this.moduleManager.onMutate();
      styleTxn.commit();
    });

    this.eventManager.emit(`update`, null);

    frameDOM.flush();
  }

  private updateDevtoolsFPS(time: number): void {
    if (this.devtoolsFpsLastSampleTime === 0) {
      this.devtoolsFpsLastSampleTime = time;
    }

    this.devtoolsFpsFrameCount += 1;
    const elapsed = time - this.devtoolsFpsLastSampleTime;

    if (elapsed < 1000) {
      return;
    }

    const fps = (this.devtoolsFpsFrameCount * 1000) / elapsed;
    this.devtools.setFPS(fps);
    this.devtoolsFpsFrameCount = 0;
    this.devtoolsFpsLastSampleTime = time;
  }

  /**
   * Handles resize events from scroll container or window.
   * Ignores height-only changes on mobile to prevent layout jumps.
   * Rebuilds layout and triggers module resize if size really changed.
   */
  public onResize(force: boolean = false, source: string = ""): void {
    if (this.canRebuild == false) {
      return;
    }
    const container = this.data.scroll.container;
    const scroll = this.context.data.scroll;
    let width = 0;
    let height = 0;
    var newScrollHeight;
    var newContainerTopPosition = 0;
    const rect = container.getBoundingClientRect();

    if (container.tagName == "BODY") {
      width = document.documentElement.clientWidth || window.innerWidth || rect.width;
      height = window.innerHeight;
    } else {
      width = rect.width;
      height = rect.height;
    }

    newContainerTopPosition = container.tagName === "BODY" ? 0 : rect.top;
    newScrollHeight = scroll.container.scrollHeight;
    const transformScale = this.tools.transformScaleParser.process({
      value: window.getComputedStyle(container).transform,
    });
    this.context.data.viewport.transformScale =
      window.getComputedStyle(container).scale == "none"
        ? transformScale
        : Number(window.getComputedStyle(container).scale);
    this.context.data.scroll.transformedCurrent =
      this.context.data.scroll.current * this.context.data.viewport.transformScale;
    const isTouch = isTouchDevice();
    const isDesktop = width > 1024;

    const widthChanged = this.prevWidth !== width;
    const heightChanged = this.prevHeight !== height;
    const heightDiff = Math.abs(this.prevHeight - height);
    const scrollHeightChanged = this.context.data.viewport.contentHeight !== newScrollHeight;

    const shouldRebuild =
      widthChanged ||
      (!isTouch && heightChanged) ||
      (isTouch && heightDiff > 150) ||
      scrollHeightChanged;

    this.context.data.scroll.topPosition = Math.floor(newContainerTopPosition);
    this.context.data.viewport.contentWidth = width;
    this.context.data.viewport.contentHeight = newScrollHeight;

    this.prevWidth = width;
    this.prevHeight = height;

    this.context.data.viewport.windowWidth = width;
    this.context.data.viewport.windowHeight = height;

    const htmlFontSize = window.getComputedStyle(document.documentElement).fontSize;
    const fontSizeNumber = parseFloat(htmlFontSize);
    this.context.data.viewport.baseRem = fontSizeNumber * transformScale;
    this.syncDebugScrollState();

    scroll.bottomPosition = this.context.data.viewport.contentHeight - height;
    // logScrollDebug("StringTune", "resize", {
    //   force,
    //   width,
    //   height,
    //   widthChanged,
    //   heightChanged,
    //   scrollHeightChanged,
    //   shouldRebuild,
    //   mode: this.data.scroll.mode,
    //   current: scroll.current,
    //   target: scroll.target,
    //   bottomPosition: scroll.bottomPosition,
    // });

    if (widthChanged || (typeof force === "boolean" && force)) {
      this.moduleManager.onResizeWidth();
    }
    if (shouldRebuild || (typeof force === "boolean" && force)) {
      const elementScrollTop = this.context.data.scroll.elementContainer.scrollTop;
      const previousTarget = this.context.data.scroll.target;
      const wasProgrammaticSmoothScroll =
        scroll.mode === "smooth" &&
        (Math.abs(scroll.target - scroll.current) > 1 || Math.abs(scroll.delta) > 0.0001);

      if (elementScrollTop > 0 || scroll.current !== 0) {
        this.context.data.scroll.current = Math.max(
          0,
          Math.min(elementScrollTop, this.context.data.scroll.bottomPosition),
        );
        // transformedCurrent was computed further up from the *old* current
        // (line ~1506); current was just reassigned from live scrollTop, so
        // transformedCurrent must be re-derived here too, or it goes stale
        // relative to current until the next scroll-engine tick happens to
        // resync it. Position fallbacks (StringModule.ts start/end) read
        // transformedCurrent, so a stale value here corrupts their calc.
        this.context.data.scroll.transformedCurrent =
          this.context.data.scroll.current * this.context.data.viewport.transformScale;
      }

      if (wasProgrammaticSmoothScroll) {
        const resolvedIntentTarget = this.activeScrollIntent
          ? this.resolveScrollIntentPosition(this.activeScrollIntent)
          : null;
        const nextTargetBase = resolvedIntentTarget ?? this.context.data.scroll.target;
        const nextTarget = Math.max(
          0,
          Math.min(nextTargetBase, this.context.data.scroll.bottomPosition),
        );
        this.context.data.scroll.target = nextTarget;

        if (Math.abs(this.context.data.scroll.target - this.context.data.scroll.current) <= 1) {
          this.context.data.scroll.target = this.context.data.scroll.current;
          this.context.data.scroll.delta = 0;
          this.context.data.scroll.lerped = 0;
        }

        // logScrollDebug("StringTune", "resize-preserve-programmatic-target", {
        //   elementScrollTop,
        //   current: this.context.data.scroll.current,
        //   previousTarget,
        //   intent: this.activeScrollIntent?.kind ?? null,
        //   resolvedIntentTarget,
        //   nextTarget: this.context.data.scroll.target,
        //   bottomPosition: this.context.data.scroll.bottomPosition,
        //   delta: this.context.data.scroll.delta,
        // });
      } else if (elementScrollTop > 0) {
        this.context.data.scroll.target = this.context.data.scroll.current;
      }

      this.moduleManager.onResize();
      if (this.scrollManager) {
        this.scrollManager.updateResponsiveMode();
      }
      this.onSettingsChange({
        isDesktop,
        widthChanged,
        heightChanged,
        scrollHeightChanged,
        isForceRebuild: force === true,
      });
      this.objectManager.refreshObservers();
      this.objectManager.invalidateInviewIndex();
      this.moduleManager.onScroll();
      this.moduleManager.onScrollMeasure();
      this.moduleManager.onFrame();
    }

    this.objectManager.checkInview();
  }

  public invalidateCenter(id: string) {
    const object = this.objectManager.all.get(id);
    if (object) this.centers.invalidate(object);
  }

  public scrollTo(position: number): void;
  public scrollTo(selector: string): void;
  public scrollTo(element: HTMLElement): void;
  public scrollTo(
    options:
      | { position: number; immediate?: boolean; offset?: number }
      | { selector: string; immediate?: boolean; offset?: number }
      | { element: HTMLElement; immediate?: boolean; offset?: number },
  ): void;
  public scrollTo(
    value:
      | number
      | string
      | HTMLElement
      | { position: number; immediate?: boolean; offset?: number }
      | { selector: string; immediate?: boolean; offset?: number }
      | { element: HTMLElement; immediate?: boolean; offset?: number },
  ): void {
    const resolved = this.resolveScrollToValue(value);
    if (resolved == null) {
      // logScrollDebug("StringTune", "scroll-to-resolve-failed", {
      //   inputType:
      //     typeof value === "string"
      //       ? "selector"
      //       : typeof value === "number"
      //         ? "position"
      //         : value instanceof HTMLElement
      //           ? "element"
      //           : "object",
      // });
      return;
    }

    // logScrollDebug("StringTune", "scroll-to-dispatch", {
    //   mode: this.data.scroll.mode,
    //   resolvedPosition: resolved.position,
    //   immediate: resolved.immediate,
    //   current: this.data.scroll.current,
    //   target: this.data.scroll.target,
    //   intent: resolved.intent.kind,
    // });
    this.activeScrollIntent = resolved.intent;
    this.scrollManager.get().scrollTo(resolved.position, resolved.immediate);
  }

  private resolveScrollToValue(
    value:
      | number
      | string
      | HTMLElement
      | { position: number; immediate?: boolean; offset?: number }
      | { selector: string; immediate?: boolean; offset?: number }
      | { element: HTMLElement; immediate?: boolean; offset?: number },
  ): { position: number; immediate: boolean; intent: ScrollIntent } | null {
    if (typeof value === "number") {
      return {
        position: value,
        immediate: false,
        intent: { kind: "position", position: value, immediate: false },
      };
    }

    if (typeof value === "string" || value instanceof HTMLElement) {
      const position = this.resolveElementScrollPosition(value);
      if (position == null) {
        return null;
      }
      return {
        position,
        immediate: false,
        intent:
          typeof value === "string"
            ? { kind: "selector", selector: value, offset: 0, immediate: false }
            : { kind: "element", element: value, offset: 0, immediate: false },
      };
    }

    const immediate = value.immediate === true;
    const offset = value.offset ?? 0;

    if ("position" in value) {
      return {
        position: value.position + offset,
        immediate,
        intent: { kind: "position", position: value.position + offset, immediate },
      };
    }

    const target = "selector" in value ? value.selector : value.element;
    const position = this.resolveElementScrollPosition(target);
    if (position == null) {
      return null;
    }

    return {
      position: position + offset,
      immediate,
      intent:
        "selector" in value
          ? { kind: "selector", selector: value.selector, offset, immediate }
          : { kind: "element", element: value.element, offset, immediate },
    };
  }

  private resolveElementScrollPosition(target: string | HTMLElement): number | null {
    const element = typeof target === "string" ? document.querySelector(target) : target;

    if (!(element instanceof HTMLElement)) {
      return null;
    }

    const host = this.data.scroll.container ?? document.body ?? document.documentElement;
    const scroller = this.data.scroll.elementContainer ?? document.documentElement;
    const rect = this.tools.transformNullify.process({ element });

    if (host === document.body || host === document.documentElement) {
      return rect.top + scroller.scrollTop;
    }

    const hostRect = host.getBoundingClientRect();
    return rect.top - hostRect.top + host.scrollTop;
  }

  private resolveScrollIntentPosition(intent: ScrollIntent): number | null {
    switch (intent.kind) {
      case "position":
        return intent.position;
      case "selector": {
        const position = this.resolveElementScrollPosition(intent.selector);
        return position == null ? null : position + intent.offset;
      }
      case "element": {
        const position = this.resolveElementScrollPosition(intent.element);
        return position == null ? null : position + intent.offset;
      }
    }
  }

  private clearActiveScrollIntent(reason: string): void {
    if (!this.activeScrollIntent) return;
    // logScrollDebug("StringTune", "clear-scroll-intent", {
    //   reason,
    //   intent: this.activeScrollIntent.kind,
    // });
    this.activeScrollIntent = null;
  }

  public destroy() {
    this.hasStarted = false;
    this.data.scroll.scrollContainer?.removeEventListener("scroll", this.onScrollBind);
    this.data.scroll.container?.removeEventListener("wheel", this.onWheelBind);

    window.removeEventListener("resize", this.onResizeBind);
    this.root.removeEventListener("mousemove", this.onMouseMoveBind);
    this.eventManager.off("dom:changed", this.onDOMChangedBind);
    this.observerContainerMutation?.disconnect();
    this.observerContainerMutation = null;
    if (this.pendingResizeRaf != null) {
      cancelAnimationFrame(this.pendingResizeRaf);
      this.pendingResizeRaf = null;
    }
    if (this.pendingRebuildRaf != null) {
      cancelAnimationFrame(this.pendingRebuildRaf);
      this.pendingRebuildRaf = null;
    }

    this.objectManager.destroy();
    this.scrollManager.destroy();
    this.devtools.destroy();
  }
}

export {
  StringTune as default,
  StringCursor,
  StringDelayLerpTracker,
  StringFPSTracker,
  StringGlide,
  StringLazy,
  StringLerp,
  StringLerpTracker,
  StringLoading,
  StringForm,
  StringImpulse,
  StringAttractor,
  StringMarquee,
  StringMagnetic,
  StringMasonry,
  StringParallax,
  StringPositionTracker,
  StringProgress,
  StringResponsive,
  StringSpotlight,
  StringTilt,
  // StringVelocity,
  StringScrollbar,
  // StringCircularText,
  StringSplit,
  StringAnchor,
  StringTune as StringTune,
  StringVideoAutoplay,
  StringModule,
  StringObject,
  StringData,
  StringSequence,
  StringScroller,
  StringProgressPart,
  StringSignal,
  StringRandom,
  CursorReactiveModule,
  ScrollController,
  StringScrollContainer,
  StringDevLayout,
  StringDevInview,
  StringDevProgress,
  StringDevModule,
  StringDevOverlayRegistry,
  StringDevIconRegistry,
  StringDevRulers,
  GridAdapter,
  // StringInfiniteVirtual,
  // StringInfiniteVirtualDOM,
  // StringVirtualScroll,
  // StringBitset,
  // StringFilterEngine,
  // StringFilterResult,
  // StringFilterSchema,
  // StringFilteredVirtualDataset,
  // createFilterSchema,
  // createFilteredVirtualDataset,
  frameDOM,
  styleTxn,
  startPointerDrag,
  type PointerDragHandlers,
  type PointerDragDelta,
  buildDevtoolsThemeBlock,
  ensureStringDevtoolsSharedStyles,
  resolveDevtoolsIcon,
  type StringRulersTrigger,
  type RulersTriggerAction,
  type RulersLayoutGrid,
  type StringDevStyleTokens,
  type ScrollMarkRule as ScrollTriggerRule,
  // type StringInfiniteVirtualDOMOptions,
  // type StringVirtualScrollSetDataPayload,
  // type StringVirtualScrollItemEventPayload,
  // type StringVirtualScrollSetFilterPayload,
  // type StringFilterExpression,
  // type StringFilterFieldDefinition,
  // type StringFilterFieldKind,
  // type StringFilterIndexKind,
  // type StringFilterPrimitive,
  // type StringFilteredVirtualDatasetItem,
  // type StringFilteredVirtualDatasetOptions,
  // type StringInfiniteVirtualOptions,
  // type StringInfiniteVirtualReachEvent,
  // type StringInfiniteVirtualSnapshot,
  type StringContext,
  type StringDevtoolDefinition,
  type StringDevtoolProvider,
  type StringDevtoolState,
  // type StringVirtualAlign,
  // type StringVirtualAxis,
  // type StringVirtualItem,
  // type StringVirtualKey,
  // type StringVirtualRange,
};

// Export performance utilities
export { DOMBatcher } from "./core/managers/DOMBatcher";
